import { useMemo } from "react";
import {
  MRT_Table,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import { useTranslation } from "react-i18next";
import AuditLogFieldComparison from "@/components/auditLog/AuditLogFieldComparison";
import type { AuditDiffClient } from "@/components/auditLog/auditDiffClient";
import type {
  AuditField,
  AuditReferenceLabels,
} from "@/components/auditLog/auditLogTypes";
import styles from "@/components/auditLog/auditLog.module.css";

interface Props {
  fields: AuditField[];
  client: AuditDiffClient;
  references: AuditReferenceLabels;
}

export default function AuditLogChangesTable({
  fields,
  client,
  references,
}: Props) {
  const { t } = useTranslation();
  const columns = useMemo<MRT_ColumnDef<AuditField>[]>(
    () => [
      {
        id: "field",
        header: t("audit_diff_field"),
        accessorFn: (field) =>
          field.name
            ? t(field.name, { defaultValue: field.name })
            : t("audit_diff_original"),
      },
      {
        id: "value",
        header: t("value"),
        Cell: ({ row }) => (
          <AuditLogFieldComparison
            field={row.original}
            index={row.index}
            client={client}
            references={references}
          />
        ),
      },
    ],
    [client, references, t]
  );
  const table = useMaterialReactTable({
    columns,
    data: fields,
    enableSorting: false,
    enablePagination: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    layoutMode: "semantic",
    initialState: { density: "compact" },
    muiTableProps: {
      "aria-label": t("audit_diff_changes"),
      sx: { tableLayout: "fixed", width: "100%" },
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        width: column.id === "field" ? "22%" : "78%",
        minWidth: 0,
        padding: "6px 8px",
        fontSize: "0.8125rem",
      },
    }),
    muiTableBodyCellProps: ({ column }) => ({
      component: column.id === "field" ? "th" : "td",
      scope: column.id === "field" ? "row" : undefined,
      sx: {
        width: column.id === "field" ? "22%" : "78%",
        minWidth: 0,
        padding: "6px 8px",
        verticalAlign: "top",
        overflowWrap: "anywhere",
        whiteSpace: "normal",
        fontSize: "0.8125rem",
        fontWeight: column.id === "field" ? 600 : 400,
      },
    }),
    muiTableBodyRowProps: { hover: false },
  });

  return (
    <>
      <div className={styles.legend} role="note">
        <span className={styles.added}>+ {t("audit_diff_added")}</span>
        <span className={styles.removed}>− {t("audit_diff_removed")}</span>
        <span>{t("audit_diff_transition_legend")}</span>
      </div>
      <MRT_Table table={table} />
    </>
  );
}
